'use client';

import React, { useMemo, useEffect, useRef, useCallback, useState } from 'react';
import { useInstantSearch, Configure } from 'react-instantsearch';
import { useIsPopNavigation } from 'commons/navState';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import useWarehouseIdForUser from 'global-state/warehouseIdForUser/useWarehouseIdForUser';
import useAdvancedSearchContext from '../../hooks/useAdvancedSearchContext';
import useFilterTransitionState from '../../hooks/useFilterTransitionState';
import AdvancedSearchProductsGrid from '../AdvancedSearchProductsGrid';
import AdvancedSearchLoadMoreButtons from '../AdvancedSearchProductsGrid/AdvancedSearchLoadMoreButtons';
import InitialProductsGrid from './InitialProductsGrid';
import ProductsGridSkeleton from './ProductsGridSkeleton';
import useHitsAccumulation from './hooks/useHitsAccumulation';
import useDisplayProducts from './hooks/useDisplayProducts';
import useScrollPersistence from './hooks/useScrollPersistence';
import { buildHitsFilterStateKey } from './utils';
const ALGOLIA_MAX_HITS_PER_PAGE = 1000;
const AdvancedSearchProductHits = ({
  onProductsChange,
  initialProducts,
  seedServerData,
  eagerFirstPageImages = false,
  enableShowAllButton = false,
  variantMatching = null
} = {}) => {
  const {
    items,
    isLastPage,
    showMore
  } = useHitsAccumulation();
  const {
    results,
    status,
    setIndexUiState,
    indexUiState,
    refresh
  } = useInstantSearch({
    skipSuspense: true
  });
  // Read the query from uiState. A useSearchBox() here registers a second
  // search box widget, and sort remounts this component (key=index). With
  // preserveSharedStateOnUnmount: false that widget's dispose clears query.
  const searchBoxQuery = indexUiState?.query || '';
  const {
    currentProductIndex,
    isReadyToShipActive,
    minWidth,
    maxWidth,
    activeTypeFilter,
    pagesUrlParam,
    updateUrlParamImmediate,
    selectedFacets
  } = useAdvancedSearchContext();
  const warehouseId = useWarehouseIdForUser();
  const isPopNavigation = useIsPopNavigation();
  const isPopRef = useRef(isPopNavigation());
  const scrollRestoredRef = useRef(false);
  const targetPagesRef = useRef(pagesUrlParam);
  const isLoadingAllRef = useRef(false);
  const showAllTargetCountRef = useRef(null);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [showAllHitsPerPage, setShowAllHitsPerPage] = useState(null);
  useEffect(() => {
    setIndexUiState(prev => {
      const currentType = prev?.refinementList?.type?.[0] ?? null;
      if (currentType === activeTypeFilter) return prev; // Already correct — no-op
      const newRefinementList = {
        ...(prev.refinementList || {})
      };
      if (activeTypeFilter) {
        newRefinementList.type = [activeTypeFilter];
      } else {
        delete newRefinementList.type;
      }
      return {
        ...prev,
        refinementList: newRefinementList
      };
    });
  }, [activeTypeFilter, setIndexUiState]);
  const currentPageCount = Math.ceil((items?.length || 0) / NUMBER_OF_PRODUCTS_TO_SHOW);
  const queryForPagination = searchBoxQuery || results?.query || '';
  const prevQueryForPaginationRef = useRef(queryForPagination);
  if (prevQueryForPaginationRef.current !== queryForPagination) {
    prevQueryForPaginationRef.current = queryForPagination;
    if (!(isPopRef.current && pagesUrlParam > 0)) {
      targetPagesRef.current = 0;
    }
  }

  // Includes query so Load More's targetPagesRef resets on a new search.
  const filterStateKey = useMemo(() => buildHitsFilterStateKey({
    query: queryForPagination,
    currentProductIndex,
    refinementList: indexUiState?.refinementList,
    isReadyToShipActive,
    minWidth,
    maxWidth,
    selectedFacets,
    warehouseId,
    variantMatching
  }), [queryForPagination, currentProductIndex, indexUiState?.refinementList, isReadyToShipActive, minWidth, maxWidth, selectedFacets, warehouseId, variantMatching]);
  const {
    isFilterTransitioning,
    hasSearchCompleted,
    showNoResults
  } = useFilterTransitionState({
    filterStateKey,
    setIndexUiState,
    items,
    status,
    activeTypeFilter,
    pagesUrlParam,
    updateUrlParamImmediate,
    isPopRef,
    scrollRestoredRef,
    targetPagesRef
  });
  useEffect(() => {
    if (status !== 'idle' || !items?.length || isLastPage) return;
    if (isLoadingAllRef.current || showAllHitsPerPage != null) return;
    if (currentPageCount - 1 < targetPagesRef.current) {
      showMore();
    }
  }, [currentPageCount, status, items?.length, isLastPage, showMore, showAllHitsPerPage]);

  // Raw server category data (products + upholsteryOptions) seeds the product
  // accumulation so displayProducts is populated at SSR/first paint — the real
  // grid renders immediately with no skeleton phase, and the client fetch
  // replaces the seeds in place with enriched data.
  const seed = useMemo(() => seedServerData?.products?.length ? {
    products: seedServerData.products,
    upholsteryOptions: seedServerData.upholsteryOptions
  } : undefined, [seedServerData]);
  const {
    displayProducts,
    loading,
    productIds,
    productIdsRaw,
    expectedCardCount
  } = useDisplayProducts({
    items,
    searchBoxQuery,
    results,
    seed,
    variantMatching
  });
  useScrollPersistence({
    isPopRef,
    scrollRestoredRef,
    targetPagesRef,
    status,
    currentPageCount,
    itemsLength: items?.length,
    loading,
    productIdsLength: productIds.length,
    productIdsRawLength: productIdsRaw.length
  });
  useEffect(() => {
    if (onProductsChange && displayProducts?.length > 0) {
      onProductsChange(displayProducts);
    }
  }, [displayProducts, onProductsChange]);
  useEffect(() => {
    isLoadingAllRef.current = false;
    showAllTargetCountRef.current = null;
    setIsLoadingAll(false);
    setShowAllHitsPerPage(null);
  }, [filterStateKey]);
  const handleShowMore = useCallback(() => {
    const nextPages = pagesUrlParam + 1;
    showMore();
    targetPagesRef.current = nextPages;
    updateUrlParamImmediate?.({
      pages: nextPages
    });
  }, [showMore, updateUrlParamImmediate, pagesUrlParam]);
  const handleShowAll = useCallback(() => {
    const totalHits = results?.nbHits ?? 0;
    if (!totalHits || totalHits <= (items?.length ?? 0)) return;
    isLoadingAllRef.current = true;
    setIsLoadingAll(true);
    const hitsPerPage = Math.min(totalHits, ALGOLIA_MAX_HITS_PER_PAGE);
    showAllTargetCountRef.current = hitsPerPage;
    setShowAllHitsPerPage(hitsPerPage);
    targetPagesRef.current = 0;
    setIndexUiState(prev_0 => ({
      ...prev_0,
      page: 0
    }));
    refresh();
  }, [results?.nbHits, items?.length, setIndexUiState, refresh]);
  useEffect(() => {
    if (!isLoadingAllRef.current) return;
    if (status !== 'idle' || loading) return;
    const requestedBatch = showAllTargetCountRef.current;
    const batchReceived = requestedBatch != null && (items?.length ?? 0) >= requestedBatch;
    const showAllComplete = isLastPage || batchReceived;
    if (!showAllComplete) return;
    isLoadingAllRef.current = false;
    showAllTargetCountRef.current = null;
    setIsLoadingAll(false);
    targetPagesRef.current = 0;
    updateUrlParamImmediate?.({
      pages: undefined
    });
  }, [isLastPage, status, loading, items?.length, updateUrlParamImmediate]);
  const isRefreshing = status === 'loading' || status === 'stalled' || loading;
  const loadMoreButtonsProps = {
    enableShowAllButton,
    onLoadMore: handleShowMore,
    onShowAll: handleShowAll,
    isLoading: loading,
    isLoadingAll,
    showLoadMore: !isLastPage
  };

  // Fallback path for callers that provide initialProducts but no
  // seedServerData. With seeding (the primary mechanism), displayProducts is
  // already populated at SSR from the seeded accumulation and this grid never
  // shows. Without seeds, these server products paint at SSR/first paint and
  // are torn down once the first search completes (hasSearchCompleted flips at
  // the first post-hydration effect flush), handing off through the same-size
  // skeleton: the client-enriched products carry hit-derived rows the server
  // payload lacks (quickship badges, showroom flags), so keeping these cards
  // mounted into the swap would grow each card when those rows appear — a
  // measured ~0.2 CLS. The parent additionally stops passing initialProducts
  // after the first real result set, so sort-triggered remounts can never
  // flash them back in.
  //
  // Suppressed while variant matching is active: these server products carry
  // each product's full colorway catalogue and a catalogue-sized
  // `moreVariantsCount`, so painting them would flash unmatched swatches and a
  // "+104 more" that the matched grid then contradicts. The SKU allowlist only
  // exists once hits are in, so there is nothing correct to preview — fall
  // through to the skeleton, which reserves the same height.
  const showInitialProducts = initialProducts?.length > 0 && !hasSearchCompleted && !variantMatching;
  if (!items?.length) {
    // Show loading skeleton if: Algolia loading, filter transitioning, search not completed yet, or within delay before "No results"
    const showSkeleton = status === 'loading' || status === 'stalled' || isFilterTransitioning || !hasSearchCompleted || !showNoResults;
    if (showSkeleton) {
      if (showInitialProducts) {
        return <InitialProductsGrid products={initialProducts} expectedCount={NUMBER_OF_PRODUCTS_TO_SHOW} loadMoreButtonsProps={loadMoreButtonsProps} />;
      }
      // Algolia count is not known yet — reserve a full page of placeholders.
      return <ProductsGridSkeleton count={NUMBER_OF_PRODUCTS_TO_SHOW} />;
    }
    return <div role="status" aria-live="polite" className="py-8 text-center">
        No results found.
      </div>;
  }

  // Algolia has returned hit IDs, but the full product details come from a
  // separate client-only GraphQL fetch (useInfiniteStaticProducts). At SSR /
  // first paint — and until that fetch resolves — `displayProducts` is empty.
  // Render the reserved-height skeleton instead of an empty div so the grid keeps
  // its height. Otherwise the grid collapses to 0px, the category description
  // below it becomes the LCP element, and it gets shoved down thousands of px
  // once products render — the source of the mobile PLP CLS (~0.7) regression.
  // The Algolia hit count is known here, so size the skeleton to it — this keeps
  // the reserved height matched to the real result even for small categories.
  // If enrichment finishes with no products (Alexandria drop / data gap), show
  // empty — never keep the previous unfiltered cards or an endless skeleton.
  if (!displayProducts?.length) {
    if (showInitialProducts) {
      return <InitialProductsGrid products={initialProducts} expectedCount={items.length} loadMoreButtonsProps={loadMoreButtonsProps} />;
    }
    const stillWaitingForEnrichment = loading || status === 'loading' || status === 'stalled' || isFilterTransitioning;
    // `expectedCardCount` is zero only when no loaded hit has a qualifying
    // SKU, so a page that can never render a card is known before enrichment
    // lands — reserving height for cards that can never arrive would just be
    // a hole.
    if (stillWaitingForEnrichment && expectedCardCount > 0) {
      return <ProductsGridSkeleton count={expectedCardCount} />;
    }
    return <>
        <div role="status" aria-live="polite" className="py-8 text-center">
          No results found.
        </div>
        {/* On a SKU-matching page every hit on a page can legitimately drop
            (no SKU qualifies at this warehouse, or none matches an enriched
            variant). Returning the empty state alone would strand the user on
            a sparse page with page 2 unreachable. The page stays sparse — we
            do not auto-advance — but Load More survives. Off a matching page a
            hit only drops on an enrichment gap, where more pages are no more
            likely to render, so the plain empty state stands alone. */}
        {variantMatching && <AdvancedSearchLoadMoreButtons {...loadMoreButtonsProps} />}
      </>;
  }
  return <div className={isRefreshing ? 'animate-pulse' : ''}>
      {showAllHitsPerPage != null && <Configure hitsPerPage={showAllHitsPerPage} />}
      {/* First-page images load eagerly on the category PLP (mirrors legacy
          ProductListingGrid). Without this, the grid that replaces the
          SSR-painted initial products mounts fresh lazy images post-paint —
          each grows from its collapsed height when pixels arrive (the config
          image has no reserved height), shifting the card below it. */}
      <AdvancedSearchProductsGrid products={displayProducts} eagerImageCount={eagerFirstPageImages ? NUMBER_OF_PRODUCTS_TO_SHOW : 0} />
      <AdvancedSearchLoadMoreButtons {...loadMoreButtonsProps} />
    </div>;
};
export default AdvancedSearchProductHits;